import { useEffect, useState } from "react";
import { Share2, Copy, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const SITE = "https://thedialstream.com";
let cache = null;

// Cached per session: which approved films/podcasts the signed-in user is the linked creator of.
function useOwns(kind, slug) {
  const { user } = useAuth();
  const [owns, setOwns] = useState(false);
  useEffect(() => {
    if (!user || !slug) { setOwns(false); return; }
    cache = cache && cache.uid === user.id ? cache : { uid: user.id, p: api.get("/creator/my-pages").then(({ data }) => data).catch(() => ({ films: [], podcasts: [] })) };
    cache.p.then((d) => setOwns((kind === "indie" ? d.films : d.podcasts).some((x) => x.slug === slug)));
  }, [user, kind, slug]);
  return owns;
}

async function copy(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const t = document.createElement("textarea"); t.value = text; document.body.appendChild(t); t.select();
    const ok = document.execCommand("copy"); t.remove(); return ok;
  }
}

function defaultPost(kind, title, url) {
  return kind === "indie"
    ? `My film "${title}" is now on The Dial — see where to watch it: ${url}`
    : `My podcast "${title}" is now on The Dial — find the show and episodes here: ${url}`;
}

export default function ShareKit({ kind, slug, title, className = "" }) {
  const owns = useOwns(kind, slug);
  const url = `${SITE}/${kind === "indie" ? "indie" : "podcasts"}/${slug}`;
  const [open, setOpen] = useState(false);
  const [post, setPost] = useState("");
  const [copied, setCopied] = useState("");
  if (!owns) return null;

  const show = () => { setPost(defaultPost(kind, title, url)); setCopied(""); setOpen(true); };
  const doCopy = async (what, text) => { if (await copy(text)) { setCopied(what); toast.success(what === "link" ? "Link copied" : "Post copied"); } else toast.error("Couldn't copy"); };
  const nativeShare = async () => { try { await navigator.share({ title, text: post }); } catch { /* dismissed */ } };
  const btn = "inline-flex items-center justify-center gap-2 rounded-[var(--card-radius)] px-4 py-2.5 text-sm font-semibold transition-colors";

  return (
    <>
      <button type="button" onClick={show} className={`${btn} border border-[var(--border-accent)] text-[var(--accent-hover)] hover:bg-[var(--accent)] hover:text-[var(--accent-contrast)] ${className}`} data-testid="share-kit-open">
        <Share2 size={16} /> Share your page
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md grid-cols-[minmax(0,1fr)] rounded-lg border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[var(--text-primary)]" data-testid="share-kit-dialog">
          <DialogHeader>
            <DialogTitle>Share your page</DialogTitle>
            <DialogDescription className="text-[var(--text-secondary)]">Your public page anyone can open, no sign-in needed. Edit the post, then copy or share it.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2">
            <Link2 size={15} className="flex-none text-[var(--text-muted)]" />
            <span className="min-w-0 flex-1 truncate text-sm" data-testid="share-kit-url">{url}</span>
          </div>
          <textarea value={post} onChange={(e) => setPost(e.target.value)} rows={4} maxLength={500}
            className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="share-kit-post" />
          <div className="grid gap-2 sm:grid-cols-3">
            <button type="button" onClick={() => doCopy("link", url)} className={`${btn} border border-[var(--border-subtle)] hover:border-[var(--accent)]`} data-testid="share-kit-copy-link">{copied === "link" ? <Check size={15} /> : <Link2 size={15} />} Copy link</button>
            <button type="button" onClick={() => doCopy("post", post)} className={`${btn} border border-[var(--border-subtle)] hover:border-[var(--accent)]`} data-testid="share-kit-copy-post">{copied === "post" ? <Check size={15} /> : <Copy size={15} />} Copy post</button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button type="button" onClick={nativeShare} className={`${btn} bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]`} data-testid="share-kit-native">
                <Share2 size={15} /> Share
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
